// npm reports @capgo/inappbrowser as moved to @capgo/capacitor-inappbrowser.
// The rename cannot be taken on its own: every published version of the new name
// requires @capacitor/core >= 8 and this app is on 7. 7.29.4 is the newest
// release of the same codebase that supports Capacitor 7 — pinned, not stale.
//
// Take it with the Capacitor 8 upgrade, which is a costed ticket rather than a
// wall: every Capacitor-family package here has an 8.x release. core/android/ios
// move together to 8.5.x, core must be >= 8.0.2 (camera-preview's peer), and the
// Cordova plugins are unaffected.
import { InAppBrowser, ToolBarType } from "@capgo/inappbrowser";
import type { PluginListenerHandle } from "@capacitor/core";
import { AssignmentBrowser } from "refsix-assignment-browser";
import type { AssignmentBrowserPlugin } from "refsix-assignment-browser";
import type { AssignmentPlatform } from "refsix-core";
import { isIos } from "../platformDetection";
import { installMockInAppBrowser } from "./mockInAppBrowser";

/**
 * The WebView an import runs in, whichever plugin provides it.
 *
 * - iOS: `refsix-assignment-browser`, our own plugin. It takes a platform id
 *   and nothing else; the URL and the injected scraper are bundled into the
 *   binary, because App Review (guideline 2.5.2) rejected @capgo/inappbrowser's
 *   run-any-script-on-any-URL surface in an app whose web bundle is updated
 *   over the air. `capacitor.config.ts` keeps capgo out of the iOS build.
 * - Android: @capgo/inappbrowser, injecting the scraper fetched from this
 *   web bundle on every page load.
 * - Web with REACT_APP_MOCK_IN_APP_BROWSER: the stand-in, in either shape.
 *
 * Everything else about an import — what a message means, when to redirect,
 * the timeout, reporting, the POST — is shared, in inAppAssignmentImport.ts.
 */
export interface ScheduleBrowser {
    /** Registers the import's listeners. The caller removes them. */
    listen(events: ScheduleBrowserEvents): Promise<PluginListenerHandle[]>;
    open(platform: AssignmentPlatform): Promise<void>;
    /** Navigates to the platform's own schedule page. */
    goToSchedule(platform: AssignmentPlatform): Promise<void>;
    close(): Promise<void>;
}

export interface ScheduleBrowserEvents {
    /** Whatever arrived on the bridge, unparsed. */
    onMessage: (detail: unknown) => void;
    /** The user closed the WebView. */
    onClosed: () => void;
}

export type CapgoBrowserApi = Pick<
    typeof InAppBrowser,
    "addListener" | "openWebView" | "executeScript" | "close" | "setUrl"
>;

export type AssignmentBrowserApi = Pick<
    AssignmentBrowserPlugin,
    "addListener" | "open" | "goToSchedule" | "close"
>;

/**
 * Where the built scraper lands — see scripts/build-assignments-scraper.js.
 *
 * Absolute, as `/r6watch/index.html` is: this page's own URL is a deep route
 * ("…/more/integrations/moas"), so a relative path would resolve against that
 * and 404.
 */
const SCRAPER_URL = "/injected/assignments-scraper.js";

/**
 * The scraper as text, ready for `executeScript`. Android only: iOS injects the
 * copy bundled into the binary.
 *
 * Fetched from the app's own origin rather than bundled as a string constant, so
 * the scrape bundle does not ride along in the app bundle for every user who
 * never opens this page.
 */
async function scraperCode(): Promise<string> {
    const response = await fetch(SCRAPER_URL);
    if (!response.ok) {
        // Generated at build time, so this is a broken build rather than
        // anything the user did — and it is worth saying so plainly, because the
        // symptom otherwise is a WebView that never finds a schedule.
        throw new Error(
            `The assignment scraper is missing (${response.status}). ` +
                "Run `npm run assignments-scraper` in phone/."
        );
    }
    return response.text();
}

export function capgoScheduleBrowser(
    api: CapgoBrowserApi,
    code: string
): ScheduleBrowser {
    return {
        listen: async ({ onMessage, onClosed }) => [
            await api.addListener("messageFromWebview", (event) =>
                onMessage(event?.detail)
            ),
            await api.addListener("browserPageLoaded", () => {
                // Injected per page load rather than as a preShowScript: by the
                // time this fires the native side has installed the
                // `window.mobileApp` bridge, and the schedule is reached by
                // navigating (login, then the appointments list), so there is
                // no single page to inject into up front.
                api.executeScript({ code }).catch((err) =>
                    console.warn("Could not inject the assignment scraper", err)
                );
            }),
            await api.addListener("closeEvent", () => onClosed()),
        ],

        open: async (platform) => {
            await api.openWebView({
                url: platform.scheduleUrl,
                title: platform.displayName,
                // The user has to be able to get out, and to retry a page that
                // failed to load on a bad connection.
                toolbarType: ToolBarType.ACTIVITY,
                showReloadButton: true,
                // A universal link would hand the sign-in off to another app,
                // stranding the session outside the WebView that needs it.
                preventDeeplink: true,
            });
        },

        goToSchedule: async (platform) => {
            await api.setUrl({ url: platform.scheduleUrl });
        },

        close: () => api.close(),
    };
}

export function nativeScheduleBrowser(
    api: AssignmentBrowserApi
): ScheduleBrowser {
    return {
        listen: async ({ onMessage, onClosed }) => [
            await api.addListener("scrapeMessage", (event) =>
                onMessage(event?.detail)
            ),
            await api.addListener("closed", () => onClosed()),
        ],
        open: (platform) => api.open({ platformId: platform.id }),
        goToSchedule: () => api.goToSchedule(),
        close: () => api.close(),
    };
}

/**
 * The browser for this device.
 *
 * The mock is resolved per call, not captured at module load: it is installed
 * during app init, which can be after this module is first imported. Installing
 * here too (it is idempotent) means the stand-in is in place however app init
 * is ordered. The seam is here rather than at Capacitor because
 * `registerPlugin` overwrites `Capacitor.Plugins.<name>` with its own proxy —
 * see `mockInAppBrowser.ts`.
 */
export async function scheduleBrowser(): Promise<ScheduleBrowser> {
    const mock = installMockInAppBrowser();
    if (mock) {
        return mock.shape === "assignmentBrowser"
            ? nativeScheduleBrowser(
                  mock.assignmentBrowser as unknown as AssignmentBrowserApi
              )
            : capgoScheduleBrowser(
                  mock as unknown as CapgoBrowserApi,
                  await scraperCode()
              );
    }

    if (isIos()) {
        return nativeScheduleBrowser(AssignmentBrowser);
    }

    return capgoScheduleBrowser(InAppBrowser, await scraperCode());
}
